import {
  ErrorRounded,
  WarningRounded,
  VisibilityRounded,
  VisibilityOffRounded,
} from '@mui/icons-material';
import {
  Typography,
  Box,
  FormControl,
  InputLabel,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox,
  Divider,
} from '@mui/material';
import { useFormik } from 'formik';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { TAPIResponseStatus } from '~/api/baseFetch/common.type';
import {
  TOneClubGetUserTokenCode,
  TOneClubGetUserTokenData,
} from '~/api/oneClub/oneclub.type';
import { pxToRem } from '~/theme/typography';
import { loginErrorMessage, routePath } from '~/utils/constants';
import { login } from '~/views/Login/Login.style';
import { useLazyGetUserTokenQuery } from '~/api/oneClub';
import { useLazyGetOwnInfoQuery } from '~/api/oneBoxV3';
import { useLazyAddQrcodeIdQuery } from '~/api/onePass';
import { useLocation, useNavigate } from 'react-router-dom';
import { getParamValue } from '~/utils/helper/route';
import {
  ActionContainer,
  ForgetPasswordText,
  LoginFormHelperText,
  LoginFormInput,
  MainContainer,
  OverallError,
  SubmitButton,
} from '~/components/Login/LoginForm/LoginForm.style';
import { loginValidation } from '~/utils/validation';
import { ONEPASS_STATUS, TLoginData } from '~/components/Login/Login.type';
import {
  initialLoginForm,
  LoginProp,
} from '~/components/Login/LoginForm/LoginForm.constant';
import { useCommon } from '~/utils/hook/useCommon';
import { Authority, keyCookie } from '~/types/enum';
import { deleteCookie, setCookie } from '~/utils/cookie';

type TProps = {
  setOnePassStatus: Dispatch<SetStateAction<ONEPASS_STATUS>>;
};

export const LoginForm = ({ setOnePassStatus }: TProps) => {
  const { search } = useLocation();
  const {
    getRememberedUsername,
    setRememberedUsername,
    deleteRememberedUsername,
    setToken,
  } = useCommon();
  const { uuid } = getParamValue(search.slice(1));
  const navigate = useNavigate();
  const [isShowPassword, setIsShowPassword] = useState<boolean>(false);
  const [getUserToken] = useLazyGetUserTokenQuery();
  const [getOwnInfo] = useLazyGetOwnInfoQuery();
  const [addQrcodeId] = useLazyAddQrcodeIdQuery();
  const formik = useFormik({
    initialValues: initialLoginForm,
    validationSchema: loginValidation,
    onSubmit: async values => {
      await handleLogin(values);
    },
  });

  const {
    errors,
    touched,
    setErrors,
    resetForm,
    setFieldValue,
    values,
    handleChange,
    handleSubmit,
  } = formik;

  const hasInvalidInput = useMemo(
    () => ({
      username: !!(
        touched.username &&
        (errors[`${LoginProp.username}`] === loginErrorMessage.emptyAccount ||
          errors[`${LoginProp.username}`] === loginErrorMessage.invalidFormat)
      ),
      password: !!(
        touched.username &&
        (errors[`${LoginProp.password}`] === loginErrorMessage.emptyPassword ||
          errors[`${LoginProp.password}`] === loginErrorMessage.invalidFormat)
      ),
    }),
    [errors, touched],
  );

  const handleLogin = async ({
    username,
    password,
    isRememberUsername,
  }: TLoginData) => {
    deleteCookie(keyCookie.ONEPASS_MOE_WEB);
    const res = await getUserToken({
      username,
      password,
      from: 'Nani',
    }).unwrap();
    const { code, data, jwt } = res;
    if (code === TOneClubGetUserTokenCode.failure) {
      if (
        data === TOneClubGetUserTokenData.PASSWORD ||
        data === TOneClubGetUserTokenData.USER
      ) {
        setErrors({
          username: loginErrorMessage.wrongInput,
          password: loginErrorMessage.wrongInput,
        });
      } else {
        setErrors({
          username: loginErrorMessage.unknown,
          password: loginErrorMessage.unknown,
        });
      }
      return;
    }

    if (jwt) {
      setToken({ code, data, jwt }); // 寫入 cookie，供 oneBoxV3BaseFetch 取用
      resetForm();
      const { data: resProfile } = await getOwnInfo().unwrap();

      if (resProfile) {
        const { authority } = resProfile;
        if (authority === Authority.CLIENT) {
          setErrors({
            username: loginErrorMessage.permissionDenied,
            password: loginErrorMessage.permissionDenied,
          });
        } else {
          if (isRememberUsername) {
            setRememberedUsername(username);
          } else {
            deleteRememberedUsername();
          }
          if (uuid) {
            // 有 id 的頁面才是 from onePass
            const resId = await addQrcodeId({
              id: uuid,
              token: jwt,
            }).unwrap();

            if (resId.data && resId.status === TAPIResponseStatus.success) {
              setOnePassStatus(ONEPASS_STATUS.success);
            } else {
              setOnePassStatus(ONEPASS_STATUS.ERROR);
            }
          } else {
            navigate(routePath.resource, { replace: true });
          }
        }
      }
    }
  };

  useEffect(() => {
    void (async () => {
      const rememberedUsername = await getRememberedUsername();
      void setFieldValue(LoginProp.username, rememberedUsername || '');
      void setFieldValue(LoginProp.isRememberUsername, !!rememberedUsername);
    })();
  }, []);

  useEffect(() => {
    if (uuid) {
      setCookie(keyCookie.ONEPASS_MOE_WEB, uuid);
    }
  }, [uuid]);

  return (
    <MainContainer>
      {errors[`${LoginProp.username}`] === loginErrorMessage.unknown ||
        errors[`${LoginProp.username}`] === loginErrorMessage.wrongInput ||
        (errors[`${LoginProp.username}`] ===
          loginErrorMessage.permissionDenied && (
          <OverallError>
            <ErrorRounded
              sx={{
                color: 'error.main',
                fontSize: pxToRem(20),
              }}
            />
            <Typography fontSize={pxToRem(14)} color="error.darker">
              {errors[`${LoginProp.username}`]}
            </Typography>
          </OverallError>
        ))}
      <Box>
        <login.title>南一帳號</login.title>
        <login.subTitle>歡迎一起加入南一，享受更多學習資源</login.subTitle>
      </Box>
      <FormControl fullWidth variant="outlined">
        <InputLabel htmlFor="username">帳號</InputLabel>
        <LoginFormInput
          id="username"
          name="username"
          error={
            hasInvalidInput.username ||
            !!(
              touched[`${LoginProp.username}`] &&
              errors[`${LoginProp.username}`]
            )
          }
          label="帳號"
          onChange={handleChange}
          onKeyUp={e => {
            e.key === 'Enter' && handleSubmit();
          }}
          value={values.username}
        />
        {touched[`${LoginProp.username}`] &&
          errors[`${LoginProp.username}`] && (
            <LoginFormHelperText>
              <WarningRounded sx={{ fontSize: pxToRem(16) }} />
              {errors[`${LoginProp.username}`]}
            </LoginFormHelperText>
          )}
      </FormControl>
      <FormControl fullWidth variant="outlined">
        <InputLabel htmlFor="password">密碼</InputLabel>
        <LoginFormInput
          error={
            hasInvalidInput.password ||
            !!(
              touched[`${LoginProp.password}`] &&
              errors[`${LoginProp.password}`]
            )
          }
          id="password"
          name="password"
          onChange={handleChange}
          onKeyUp={e => {
            e.key === 'Enter' && handleSubmit();
          }}
          value={values.password}
          type={isShowPassword ? 'text' : 'password'}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                onClick={() => {
                  setIsShowPassword(prev => !prev);
                }}
                onMouseDown={event => event.preventDefault()}
                edge="end"
              >
                {isShowPassword ? (
                  <VisibilityRounded />
                ) : (
                  <VisibilityOffRounded />
                )}
              </IconButton>
            </InputAdornment>
          }
          label="密碼"
        />
        {touched[`${LoginProp.username}`] &&
          errors[`${LoginProp.password}`] && (
            <LoginFormHelperText>
              <WarningRounded sx={{ fontSize: pxToRem(16) }} />
              {errors[`${LoginProp.password}`]}
            </LoginFormHelperText>
          )}
      </FormControl>
      <ActionContainer>
        <FormControlLabel
          control={
            <Checkbox
              name="isRememberUsername"
              onChange={handleChange}
              checked={values.isRememberUsername}
            />
          }
          label="記住帳號"
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <ForgetPasswordText
            href="https://oneclub.oneclass.com.tw/forgetpassword/"
            target="_blank"
            rel="noreferrer"
          >
            忘記密碼
          </ForgetPasswordText>
          <Divider orientation="vertical" sx={{ height: '20px' }} />
          <ForgetPasswordText
            href="https://oneclub.oneclass.com.tw/signup/"
            target="_blank"
            rel="noreferrer"
          >
            註冊帳號
          </ForgetPasswordText>
        </Box>
      </ActionContainer>
      <SubmitButton
        variant="contained"
        onClick={() => {
          handleSubmit();
        }}
      >
        登入
      </SubmitButton>
    </MainContainer>
  );
};
